import React, { useState, useEffect, useRef } from "react";
import { Chip } from "primereact/chip";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import ManageTag from "./ManageTag";
import { useDispatch, useSelector } from "react-redux";
import "./tagStyle.css";
import {
  fetchAllTags,
  assignTagToCustomer,
  removeTagFromCustomer,
  fetchAssignedTagsByCustomer,
} from "../../../reducers/tagslices";

const MANAGE_TAG_OPTION = { id: "__manage__", label: "Manage Tags" };

const ChatTagSelector = ({ visible, onhide, mobNumber }) => {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const withFullOpacity = (color) =>
    typeof color === "string" && color.length === 9
      ? `${color.slice(0, 7)}4D`
      : `${color}4D`;
  const getToastMessage = (res, fallback) => {
    const msg = res?.message;
    if (typeof msg === "string") return msg;
    if (msg && typeof msg === "object" && typeof msg.message === "string") {
      return msg.message;
    }
    return fallback;
  };

  // ✅ Local state
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showManageTags, setShowManageTags] = useState(false);

  // ✅ Redux store
  const { tags = [], loading } = useSelector((state) => state.tag);

  console.log("All tags from store:", tags);

  // Fetch all tags on mount
  useEffect(() => {
    dispatch(fetchAllTags());
  }, [dispatch]);

  // Refresh tags after closing Manage Tags modal
  useEffect(() => {
    if (!showManageTags) {
      dispatch(fetchAllTags());
    }
  }, [showManageTags, dispatch]);

  // Fetch assigned tags for customer
  useEffect(() => {
    if (mobNumber) {
      dispatch(fetchAssignedTagsByCustomer(mobNumber.replace(/\D+/g, "")))
        .unwrap()
        .then((data) => {
          const assignedTags =
            (data || [])
              .map((id) => tags?.find((tag) => tag.id === id))
              .filter(Boolean)
              .map((tag) => ({
                id: tag.id,
                label: tag.tagName,
                color: tag.tagColor,
              })) || [];
          setSelectedTags(assignedTags);
        })
        .catch((err) => console.error("Error fetching assigned tags:", err));
    }
  }, [mobNumber, dispatch, tags]);

  // Dropdown options
  const options = [
    ...(tags?.map((tag) => ({
      id: tag.id,
      label: tag.tagName,
      color: tag.tagColor,
    })) || []),
    MANAGE_TAG_OPTION,
  ];

  // Assign tag to customer
  const handleAssignTag = async (tag) => {
    if (!tag?.id || !mobNumber) return;

    // Update local state
    if (!selectedTags.some((t) => t.id === tag.id)) {
      setSelectedTags([...selectedTags, tag]);
    }

    // Call API
    const payload = {
      customerMobile: mobNumber.replace(/\D+/g, ""),
      tagId: tag.id,
    };

    try {
      const res = await dispatch(assignTagToCustomer(payload)).unwrap();
      toast.current?.show({
        severity: res?.success ? "success" : "error",
        detail: getToastMessage(res, "Tag assigned"),
      });
    } catch (err) {
      console.error("❌ Assign tag error:", err.message);
      toast.current?.show({
        severity: "error",
        detail: err?.message || "Assign tag failed",
      });
    }

    setSelectedOption(null);
  };

  // Remove tag from customer
  const handleRemoveTag = async (tag) => {
    setSelectedTags(selectedTags.filter((t) => t.id !== tag.id));

    if (!tag?.id || !mobNumber) return;

    const payload = {
      customerMobile: mobNumber.replace(/\D+/g, ""),
      tagId: tag.id,
    };

    try {
      const res = await dispatch(removeTagFromCustomer(payload)).unwrap();
      toast.current?.show({
        severity: res?.success ? "success" : "error",
        detail: getToastMessage(res, "Tag removed"),
      });
    } catch (err) {
      console.error("❌ Remove tag error:", err.message);
      toast.current?.show({
        severity: "error",
        detail: err?.message || "Remove tag failed",
      });
    }
  };

  // Dropdown item template
  const itemTemplate = (option) => {
    if (option.id === "__manage__") {
      return (
        <div className="border-t">
          <button
            type="button"
            onClick={() => setShowManageTags(true)}
            className="
              w-full flex items-center justify-center gap-2
              py-2 text-sm font-medium text-blue-600 hover:bg-blue-50
            "
          >
            <i className="pi pi-cog text-sm" />
            <span>Manage Tags</span>
          </button>
        </div>
      );
    }
    return <div className="px-3 py-2 text-sm">{option.label}</div>;
  };

  return (
    <div className="w-full flex flex-wrap items-center gap-1">
      <Toast ref={toast} />
      {/* Selected Tags */}
      {selectedTags?.map((tag) => (
        <Chip
          key={tag.id}
          label={tag.label}
          removable
          onRemove={() => handleRemoveTag(tag)}
          style={{
            backgroundColor: withFullOpacity(tag.color),
            color: "#111",
            fontSize: "0.65rem",
          }}
          className="tag-chip px-2 py-0.5 leading-none relative py-1"
        />
      ))}

      {/* Inline CSS for hover remove icon */}
      <style>
        {`
          .p-chip .p-chip-remove {
            opacity: 0;
            transition: opacity 0.2s;
          }
          .p-chip:hover .p-chip-remove {
            opacity: 1;
          }
        `}
      </style>

      {/* Dropdown */}
      <Dropdown
        value={selectedOption}
        options={options}
        optionLabel="label"
        onChange={(e) => {
          const tag = e.value;
          if (tag?.id === "__manage__") {
            setShowManageTags(true);
            return;
          }
          handleAssignTag(tag);
        }}
        itemTemplate={itemTemplate}
        filter
        placeholder="Assign Tag"
        className="tag-dropdown text-xs"
        panelClassName="tag-dropdown-panel"
      />

      {/* Manage Tags Modal */}
      <ManageTag
        visible={showManageTags}
        onHide={() => setShowManageTags(false)}
      />
    </div>
  );
};

export default ChatTagSelector;
